import React from "react";
import { t } from "i18next";
import {
    RefreshIcon,
    StyledIconButton,
    TrashIcon,
} from "@aureskonnect/react-ui";
import ReactSwitch from "react-switch";
import swal from "@sweetalert/with-react";
import classnames from "classnames";

import { setDeletedId, setIsDeleteModalOpened, setPath } from "../store";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { templateActions } from "@pages/Applications/C&C/C&CSetup/templateStore";
import { Styles } from "../Styles";
import { Table } from "../../../Common/Table";
import { validUploadedTypes } from "../../constants";
import LanguageTable from "../../../Common/LanguageTable";
import { templateDefaultValue } from "@constants/template";
import {
    checkDimensions,
    extractDimensions,
    validateFileContent,
} from "../../../helper";
import { ImageUploadFilledIcon } from "@components/Common/SvgIcons/ImageUploadFilledIcon";
import { ImageUploadIcon } from "@components/Common/SvgIcons/ImageUploadIcon";

export function BannerTable({
    deviceType,
    setDeviceType,
    section,
    path,
}: any): JSX.Element {
    const uploadInputRef = React.useRef<HTMLInputElement>(null);
    const isUploadingRef = React.useRef(false);

    const currentRowIdRef = React.useRef<string | null>(null);
    const currentRowIndexRef = React.useRef<number | null>(null);
    const isAddingNewRef = React.useRef(false);

    const [warnings, setWarnings] = React.useState<
        Record<string, Record<string, boolean>>
    >({});

    const getSizeForRow = (index: number): string => {
        if (deviceType === "Desktop") {
            switch (index + 1) {
                case 1:
                    return "1090*500 (png,jpg,jpeg ou webp)";
                case 2:
                    return "720*500 (png,jpg,jpeg ou webp)";
                case 3:
                    return "1400*400 (png,jpg,jpeg ou webp)";
                default:
                    return "450*465 (png,jpg,jpeg ou webp)";
            }
        } else {
            switch (index + 1) {
                case 3:
                    return "450*400 (png,jpg,jpeg ou webp)";
                default:
                    return "450*465 (png,jpg,jpeg ou webp)";
            }
        }
    };

    const getImagesForDevice = () => {
        const sectionData = templateActions.getNestedObject(path);

        if (!sectionData) return [];

        return Object.entries(sectionData)
            .filter(([key]) => key.startsWith("Image"))
            .map(([key, value]: [string, any]) => {
                const deviceData = value[deviceType];
                const hasContent =
                    deviceData && (deviceData.Url || deviceData.Name);
                return {
                    id: key,
                    active: true,
                    content: deviceData?.Url || "",
                    originalFilename: deviceData?.Name || "",
                    type: deviceData?.Type || "",
                    extension: deviceData?.Extenxion || "",
                    warning: hasContent
                        ? warnings[key]?.[deviceType] || false
                        : false,
                };
            });
    };

    function OrderCell({ row, className }: any) {
        return (
            <div
                className="d-flex align-items-center position-relative"
                style={{ gap: "20px", padding: "0 10px" }}
            >
                <span
                    style={{ font: "normal normal 600 15px/20px Segoe UI" }}
                    className={`epb_txt_fileOrderAS ${className || ""}`}
                >
                    {row.index + 1}
                </span>
            </div>
        );
    }

    function ActionsCell({ row }: any) {
        return (
            <div className="d-flex align-items-center justify-content-end position-relative">
                <ReactSwitch
                    disabled={true}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    handleDiameter={26}
                    offColor="#f7b4b8"
                    offHandleColor="#E30613"
                    checked={true}
                    onChange={() => {
                        const sectionData = templateActions.getNestedObject(
                            path
                        );
                        const updatedData = {
                            ...sectionData,
                            [row.original.id]: {
                                ...sectionData[row.original.id],
                                [deviceType]: {
                                    ...sectionData[row.original.id][deviceType],
                                    active: !row.original.active,
                                },
                            },
                        };
                        templateActions.updateTemplate(path, updatedData);
                    }}
                    onColor="#c2eddd"
                    onHandleColor="#34C38F"
                    width={55}
                    height={20}
                    className="epb_icn_activeDesactiveAS"
                />
                <StyledIconButton
                    icon="TrashIcon"
                    onClick={() => {
                        setDeletedId(row.original.id);
                        setPath([...path, `${row.original.id}`, deviceType]);
                        setIsDeleteModalOpened(section, true);
                    }}
                    aria-label="delete"
                    className="m-0 epb_icn_archiveFileAS"
                >
                    <TrashIcon style={{ fill: "red" }} height={20} width={20} />
                </StyledIconButton>
            </div>
        );
    }

    function UploaderCell({ row }: any) {
        const imageTitle =
            row.original.originalFilename.slice(-15) || "No image";
        const hasWarning = row.original.warning === true;

        return (
            <div className="d-flex flex-column">
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    {imageTitle !== "" && imageTitle !== "No image" ? (
                        <ImageUploadFilledIcon
                            height={40}
                            width={35}
                            onClick={() =>
                                handleUploadImageOnClickEvent(
                                    row.original.id,
                                    row.index
                                )
                            }
                            className="pointer__clz"
                        />
                    ) : (
                        <ImageUploadIcon
                            height={40}
                            width={35}
                            onClick={() =>
                                handleUploadImageOnClickEvent(
                                    row.original.id,
                                    row.index
                                )
                            }
                            className="pointer__clz"
                        />
                    )}

                    <span
                        style={{
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "200px",
                        }}
                    >
                        {imageTitle}
                    </span>
                </div>
                {hasWarning && (
                    <div
                        style={{
                            fontSize: "80%",
                            color: "#f46a6a",
                        }}
                    >
                        {t(
                            "The image/video does not meet the requirements and may affect the display."
                        )}
                    </div>
                )}
            </div>
        );
    }

    function SizeCell({ row }: any) {
        return (
            <span style={{ color: "#666", fontSize: "12px" }}>
                {getSizeForRow(row.index)}
            </span>
        );
    }

    const columns = React.useMemo(
        () => [
            {
                Header: t("Rank"),
                accessor: t("Rank"),
                Cell: OrderCell,
            },
            {
                Header: t("Size"),
                accessor: "sizeContent",
                Cell: SizeCell,
            },
            {
                Header: t("Image or video"),
                accessor: "content",
                Cell: UploaderCell,
            },
            {
                Header: t("Actions"),
                accessor: t("Actions"),
                Cell: ActionsCell,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [deviceType, warnings]
    );

    const handleUploadingNewFileRequest = React.useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (isUploadingRef.current) return;
            isUploadingRef.current = true;

            e.target.value = "";

            try {
                const sectionData = templateActions.getNestedObject(path) || {};
                const isAddingNew = isAddingNewRef.current;
                const rowIdToUpdate = currentRowIdRef.current;
                const rowIndex = currentRowIndexRef.current;

                if (!validUploadedTypes.includes(file.type)) {
                    throw new Error("File type is not valid");
                }

                const isRealMedia = await validateFileContent(file);
                if (!isRealMedia) {
                    throw new Error("File content does not match its type");
                }

                const imageKeys = Object.keys(sectionData)
                    .filter((key) => key.startsWith("Image"))
                    .sort();

                if (isAddingNew) {
                    if (section === "Section1" && imageKeys.length >= 3) {
                        throw new Error(
                            "Maximum 3 images allowed for this section"
                        );
                    }

                    const newImageKey = `Image${imageKeys.length + 1}`;
                    const targetIndex = imageKeys.length;

                    templateActions.updateMediaFile(
                        [...path, newImageKey, deviceType],
                        "fileObject",
                        file
                    );

                    const expectedSize = getSizeForRow(targetIndex);
                    const dimensions = extractDimensions(expectedSize);
                    if (dimensions) {
                        const isValid = await checkDimensions(
                            file,
                            dimensions.width,
                            dimensions.height
                        );
                        setWarnings((prev) => ({
                            ...prev,
                            [newImageKey]: {
                                ...prev[newImageKey],
                                [deviceType]: !isValid,
                            },
                        }));
                    }
                } else if (rowIdToUpdate) {
                    const oldUrl =
                        sectionData[rowIdToUpdate]?.[deviceType]?.Url;
                    if (oldUrl?.startsWith("blob:")) {
                        URL.revokeObjectURL(oldUrl);
                    }
                    const targetIndex = rowIndex ?? 0;

                    templateActions.updateMediaFile(
                        [...path, rowIdToUpdate, deviceType],
                        "fileObject",
                        file
                    );

                    const expectedSize = getSizeForRow(targetIndex);
                    const dimensions = extractDimensions(expectedSize);
                    if (dimensions) {
                        const isValid = await checkDimensions(
                            file,
                            dimensions.width,
                            dimensions.height
                        );
                        setWarnings((prev) => ({
                            ...prev,
                            [rowIdToUpdate]: {
                                ...prev[rowIdToUpdate],
                                [deviceType]: !isValid,
                            },
                        }));
                    }
                }

                swal({
                    icon: "success",
                    content: <p>{t("added successfully")}!</p>,
                    buttons: false,
                    timer: 2000,
                });
            } catch (error: any) {
                swal({
                    icon: "error",
                    content: <p>{t(error.message)}</p>,
                    buttons: false,
                    timer: 2000,
                });
            } finally {
                isUploadingRef.current = false;
                isAddingNewRef.current = false;
                currentRowIdRef.current = null;
                currentRowIndexRef.current = null;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [path, deviceType, section, t]
    );

    function handleUploadImageOnClickEvent(rowId: string, rowIndex: number) {
        isAddingNewRef.current = false;
        currentRowIdRef.current = rowId;
        currentRowIndexRef.current = rowIndex;
        uploadInputRef.current?.click();
    }

    function handleUploadedDataOnChangeEvent(
        event: React.ChangeEvent<HTMLInputElement>
    ) {
        const file = event.target.files?.[0];
        if (file) {
            handleUploadingNewFileRequest(event).finally(() => {
                if (uploadInputRef.current) uploadInputRef.current.value = "";
            });
        }
    }

    function handleResetOnClickEvent(): void {
        const sectionData = templateActions.getNestedObject(path);
        const resetData = { ...sectionData };
        Object.keys(resetData)
            .filter((key) => key.startsWith("Image"))
            .forEach((key) => {
                const url = resetData[key]?.[deviceType]?.Url;
                if (url?.startsWith("blob:")) {
                    URL.revokeObjectURL(url);
                }
                resetData[key] = {
                    ...resetData[key],
                    [deviceType]: {},
                };
            });
        templateActions.updateTemplate(path, resetData);

        // Clear all local warnings for this device type
        setWarnings((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((key) => {
                if (next[key]?.[deviceType] !== undefined) {
                    next[key] = { ...next[key] };
                    delete next[key][deviceType];
                }
            });
            return next;
        });

        if (uploadInputRef.current) {
            uploadInputRef.current.value = "";
        }
    }

    return (
        <div
            className="d-flex flex-column cursor__clz"
            style={{ gap: "10px", overflowY: "auto" }}
        >
            <span
                style={{
                    top: "630px",
                    left: "321px",
                    width: "153px",
                    height: "21px",
                    textAlign: "left",
                    font: "normal normal 600 16px/21px Segoe UI",
                    letterSpacing: "0px",
                    color: "#000000",
                    opacity: "1",
                    marginLeft: "3%",
                    marginTop: "1%",
                }}
            >
                {section === "Bannier" ? t("Banner image") : t("Images")}
            </span>
            <div className="d-flex justify-content-between align-items-center">
                <select
                    value={deviceType}
                    onChange={(e: any) =>
                        setDeviceType(e.target.value as "Desktop" | "Mobile")
                    }
                    className="form-select ml-4 py-1 px-2 rounded-sm text-xl"
                    style={{ width: "200px", background: "center" }}
                >
                    <option value="Desktop">Desktop</option>
                    <option value="Mobile">Mobile</option>
                </select>
                <div
                    className="d-flex align-items-center"
                    style={{ gap: "20px" }}
                >
                    <StyledIconButton
                        title={t("Reset to default settings")}
                        outline={true}
                        variant="danger"
                        className="m-0 p-0 epb_icn_resetModificationsAS"
                        icon="RefreshIcon"
                        onClick={handleResetOnClickEvent}
                    >
                        <RefreshIcon height={20} width={20} />
                    </StyledIconButton>
                    <div style={{ display: "none" }}>
                        <input
                            accept="video/*,image/*"
                            multiple={false}
                            type="file"
                            id="fileUpload"
                            hidden={true}
                            ref={uploadInputRef}
                            onChange={handleUploadedDataOnChangeEvent}
                            onClick={(event: any) => {
                                event.currentTarget.value = "";
                            }}
                        />
                        <CustomMainColorButton
                            rounded={true}
                            variant="primary"
                            onClick={() => {
                                isAddingNewRef.current = true;
                                uploadInputRef.current?.click();
                            }}
                            className={`${classnames(
                                "w-20 cmn_btn_SaveButton mr-5",
                                {
                                    "not-allowed-icon__clz":
                                        section === "Section1" &&
                                        Object.keys(
                                            templateActions.getNestedObject(
                                                path
                                            ) || {}
                                        ).filter((key) =>
                                            key.startsWith("Image")
                                        ).length === 3,
                                }
                            )}`}
                        >
                            {t("Add")}
                        </CustomMainColorButton>
                    </div>
                </div>
            </div>
            <div className="d-flex flex-column">
                {" "}
                <Styles>
                    <Table
                        columns={columns}
                        data={getImagesForDevice()}
                        deviceType={deviceType}
                        section={section}
                        path={path}
                    />
                </Styles>
                <LanguageTable
                    path={["SiteWeb", "LaindingPage", "Section1"]}
                    fields={[
                        {
                            key: "Title",
                            label: "Title",
                            placeholder: "Enter the Title",
                        },
                    ]}
                    defaultTexts={
                        templateDefaultValue.SiteWeb.LaindingPage.Section1
                    }
                    sectionTitle="Title and Description"
                />
            </div>
        </div>
    );
}
